import { ApiResult, HttpClient } from "../http.js";
import { PagedProperties, PropertyBillingParty, PropertyDto, PropertyUnitSide } from "../types/properties.js";

/**
 * Write shape for one enhed. Pass `id` to update an existing unit — keeping it stable is
 * what lets a project keep pointing at the same unit across an edit. A row with nothing
 * filled in is dropped server-side rather than rejected, so the UI can keep an empty
 * trailing "add another" row.
 */
export interface PropertyUnitPayload {
  id?: string | null;
  floor?: string | null;
  side?: PropertyUnitSide | null;
  label?: string | null;
  residentName?: string | null;
  residentPhone?: string | null;
  notes?: string | null;
}

export interface CreatePropertyPayload {
  name: string;
  /** Optional — an administrator-only building has no owner on file. */
  ownerCustomerId?: string | null;
  street?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryKey?: string | null;
  keyBoxCode?: string | null;
  gateCode?: string | null;
  alarmCode?: string | null;
  accessDescription?: string | null;
  notes?: string | null;
  administratorCustomerId?: string | null;
  /** Defaults to `'owner'`. `'administrator'` requires `administratorCustomerId`. */
  billingParty?: PropertyBillingParty;
  /** Omit to create with no units; send a list to create them alongside the property. */
  units?: PropertyUnitPayload[];
}

/**
 * PATCH shape. The API distinguishes "absent" from "explicit null":
 * omit a field to leave it alone, send `null` to clear it. That is what makes blanking a
 * stale key box code actually blank it — `undefined` drops out of the JSON body entirely.
 *
 * `units` follows the same contract as the customer party lists: omit to leave the stored
 * units untouched, send `[]` to clear them.
 */
export interface UpdatePropertyPayload {
  name?: string;
  /** Omit to leave alone; send null to clear — an owner may legitimately be removed. */
  ownerCustomerId?: string | null;
  street?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryKey?: string | null;
  keyBoxCode?: string | null;
  gateCode?: string | null;
  alarmCode?: string | null;
  accessDescription?: string | null;
  notes?: string | null;
  administratorCustomerId?: string | null;
  billingParty?: PropertyBillingParty;
  units?: PropertyUnitPayload[];
}

export interface ListPropertiesQuery {
  page?: number;
  pageSize?: number;
  /**
   * Matches name, street, postcode, city, owner and administrator. Access codes are
   * deliberately not searchable — a code must not be discoverable by guessing at it.
   */
  search?: string;
  ownerCustomerId?: string;
  administratorCustomerId?: string;
  /**
   * Narrows to properties this customer owns OR administers, in one request — unlike
   * `ownerCustomerId`/`administratorCustomerId`, which each match only that one role.
   * An administrator like Newsec owns nothing and administers everything, so a picker
   * scoped to "this customer's buildings" needs the OR, not either role alone.
   */
  customerId?: string;
}

export class PropertiesApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListPropertiesQuery = {}): Promise<ApiResult<PagedProperties>> {
    return this.http.get<PagedProperties>("/api/properties", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<PropertyDto>> {
    return this.http.get<PropertyDto>(`/api/properties/${encodeURIComponent(id)}`);
  }

  create(payload: CreatePropertyPayload): Promise<ApiResult<PropertyDto>> {
    return this.http.post<PropertyDto>("/api/properties", { body: payload });
  }

  update(id: string, payload: UpdatePropertyPayload): Promise<ApiResult<PropertyDto>> {
    return this.http.patch<PropertyDto>(`/api/properties/${encodeURIComponent(id)}`, { body: payload });
  }

  /** 409 when projects or assignments still point at the property. */
  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/properties/${encodeURIComponent(id)}`);
  }
}
